import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import React from "react";
import { ObjectListColumn } from "./types";

import { PaginatedResponse } from "@/types/common";
import { Await, Location, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Checkbox } from "../ui/checkbox";
import { ObjectListTableHead } from "./object-list-table-head";

interface ObjectListTableProps<T> {
    paginated_response: PaginatedResponse<T>;
    columns: ObjectListColumn<T>[];
    onRowNavigateTo?: (instance: T, currentLocation: Location) => string | Map<string, string>;
    allowAutoRedirect?: boolean;
    allowBatchSelection?: boolean;
    itemToId?: (item: T) => string;
    selectedItems?: T[];
    setSelectedItems?: (items: T[]) => void;
    selectAll?: boolean;
    setSelectAll?: (selectAll: boolean) => void;
}

export function ObjectListTable<T>(props: ObjectListTableProps<T>) {
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const location = useLocation();

    return (
        <Table>
            <TableHeader>
                <TableRow>
                    {props.allowBatchSelection && (
                        <TableHead className="w-[20px]">
                            <Checkbox
                                checked={props.selectAll || (Boolean(props.selectedItems?.length) && props.selectedItems?.length === props.paginated_response.results.length)}
                                onCheckedChange={checked => {
                                    if (checked === true) {
                                        props.setSelectedItems?.(props.paginated_response.results);
                                    } else {
                                        props.setSelectedItems?.([]);
                                        props.setSelectAll?.(false);
                                    }
                                }}
                            />
                        </TableHead>
                    )}
                    {props.columns
                        .filter(c => c.hidden !== true)
                        .map((column, index) => (
                            <ObjectListTableHead key={`${column.name}_${index}`} column={column} />
                        ))}
                </TableRow>
            </TableHeader>
            <React.Suspense
                fallback={
                    <TableBody>
                        <TableRow>
                            <TableCell colSpan={props.columns.length} className="py-10">
                                Récupération des éléments en cours...
                            </TableCell>
                        </TableRow>
                    </TableBody>
                }
            >
                <Await resolve={props.paginated_response} errorElement={<p>An error occurred</p>}>
                    {(paginated_response: PaginatedResponse<T>) => (
                        <TableBody className={props.onRowNavigateTo ? "cursor-pointer" : ""}>
                            {paginated_response.results.map((instance, index1) => (
                                <TableRow
                                    key={`row_${index1}_${searchParams.toString()}`}
                                    onClick={event => {
                                        if (props.onRowNavigateTo) {
                                            const navigateTo = props.onRowNavigateTo(instance, location);

                                            if (navigateTo instanceof Map) {
                                                navigateTo.forEach((v, k) => {
                                                    searchParams.set(k, v); // Update search params
                                                });
                                                if (event.metaKey || event.ctrlKey) {
                                                    const win = window.open(location.pathname + "?" + searchParams.toString(), "_blank");
                                                    win?.focus();
                                                } else {
                                                    setSearchParams(searchParams);
                                                }
                                            } else {
                                                if (event.metaKey || event.ctrlKey) {
                                                    const win = window.open(navigateTo, "_blank");
                                                    win?.focus();
                                                } else {
                                                    navigate(navigateTo);
                                                }
                                            }
                                        }
                                    }}
                                >
                                    {props.allowBatchSelection && (
                                        <TableCell className="w-[20px]">
                                            <Checkbox
                                                checked={props.selectedItems?.includes(instance)}
                                                onCheckedChange={checked => {
                                                    if (checked === true) {
                                                        props.setSelectedItems?.(props.selectedItems ? [...props.selectedItems, instance] : [instance]);
                                                    } else {
                                                        props.setSelectedItems?.(props.selectedItems?.filter(i => i !== instance) ?? []);
                                                        props.setSelectAll?.(false);
                                                    }
                                                }}
                                                onClick={e => e.stopPropagation()}
                                            />
                                        </TableCell>
                                    )}
                                    {props.columns
                                        .filter(c => c.hidden !== true)
                                        .map((column, index2) => {
                                            let _item = null;
                                            if (column.cellFn) {
                                                _item = column.cellFn(instance);
                                            } else if (column.accessorFn) {
                                                _item = (
                                                    <TableCell className={column.cellClassNameFn ? column.cellClassNameFn(instance) : (column.cellClassName ?? "")}>
                                                        {column.accessorFn(instance)}
                                                    </TableCell>
                                                );
                                            } else if (column.accessorKey) {
                                                _item = (
                                                    <TableCell className={column.cellClassNameFn ? column.cellClassNameFn(instance) : (column.cellClassName ?? "")}>
                                                        {instance[column.accessorKey] ? `${instance[column.accessorKey]}` : null}
                                                    </TableCell>
                                                );
                                            }
                                            return <React.Fragment key={`cell_${index1}_${index2}_${searchParams.toString()}`}>{_item}</React.Fragment>;
                                        })}
                                </TableRow>
                            ))}
                        </TableBody>
                    )}
                </Await>
            </React.Suspense>
        </Table>
    );
}
